/**
 * Canonical templating logic for agent system prompts.
 *
 * Authors write Liquid conditionals so the agent's opening line adapts to
 * whether a dynamic variable was supplied:
 *
 *   {% if first_name %}Kya main {{ first_name }} ji se baat kar rahi hoon?
 *   {% else %}Hello, kya main aapka naam jaan sakti hoon?{% endif %}
 *
 * The AUTHORITATIVE render happens at call time in `pipecat/prompt_template.py`
 * (python-liquid). Everything here is the authoring side: validation, variable
 * analysis, lint, and the dashboard's live preview. The two must agree, which
 * is what `prompt-template.fixtures.json` exists to enforce — read the contract
 * note at the top of that file before changing any engine option below.
 *
 * Imported by the React dashboard via `src/lib/prompt-template.ts`, the same
 * cross-boundary re-export pattern as `billing-config.ts`. Do NOT copy this
 * file into the app — the copy-paste "mirror" convention used by
 * `dynamic-variables.ts` is what produced four divergent `{{var}}` regexes.
 */

import { Liquid } from "npm:liquidjs@10.27.2";
import { extractDynamicVariables } from "./dynamic-variables.ts";

/** Keep in lockstep with the pin in verbeo-dashboard-react/package.json. */
export const LIQUID_VERSION = "10.27.2";

/** Resolved by the bot at call time; never supplied by the caller. */
const SYSTEM_VARIABLES = new Set(["customer_number", "assistant_number"]);

/** Tags that resolve a partial from somewhere. All must be unreachable. */
const BLOCKED_TAGS = [
  "include",
  "include_relative",
  "render",
  "layout",
  "extends",
  "block",
];

/**
 * The opt-in signal: a `{% %}` tag, or a filter pipe inside an output statement.
 *
 * Deliberately narrow, and it must stay byte-identical to `uses_liquid_syntax`
 * in pipecat/prompt_template.py. A bare `{{ name }}` is NOT an opt-in, and
 * neither is `{{ contact.first_name }}` — plenty of imported prompts contain
 * dotted names that never resolved under the old engine, and rendering them
 * through Liquid would change what the caller hears. An untouched prompt has to
 * behave exactly as it did before.
 */
const LIQUID_SYNTAX_RE = /\{%|\{\{[^{}]*\|[^{}]*\}\}/;

export function usesLiquidSyntax(src: string): boolean {
  return LIQUID_SYNTAX_RE.test(src || "");
}

export type PromptMode = "legacy" | "liquid";

export interface PromptSyntaxError {
  message: string;
  row: number;
  col: number;
  offset: number;
}

export type PromptLintRule =
  | "invalid-identifier"
  | "blank-comparison"
  | "nested-path"
  | "numeric-comparison";

export interface PromptLintWarning {
  rule: PromptLintRule;
  message: string;
  offset: number;
  length: number;
}

export interface PromptAnalysis {
  /** `liquid` once the prompt contains any `{% %}` tag; `legacy` otherwise. */
  mode: PromptMode;
  error: PromptSyntaxError | null;
  /** Every user-suppliable name referenced, in first-appearance order. */
  variables: string[];
  /** Referenced outside every conditional — the caller must supply these. */
  required: string[];
  /** Only ever referenced inside a conditional — safe to omit. */
  optional: string[];
  warnings: PromptLintWarning[];
}

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

/**
 * A Liquid engine hardened for untrusted, user-authored templates.
 *
 * Every option here is pinned rather than defaulted, because the Python side
 * pins the same values and a silent default change on either side is a
 * preview-vs-live-call divergence.
 */
export function createPromptEngine(): Liquid {
  const engine = new Liquid({
    // THE most important line. Shopify truthiness: "" and 0 are TRUTHY.
    // "blank means absent" comes from buildRenderContext stripping blank
    // values, NOT from the engine. If this flips to JS truthiness, `{% if x %}`
    // quietly changes meaning for every agent.
    jsTruthy: false,
    // Matches python-liquid's lax Undefined: a missing variable renders "".
    strictVariables: false,
    // Matches python-liquid's strict_filters=False. With strict filters one
    // typo'd filter name aborts the render and kills every conditional in the
    // prompt; the editor's lint is the right place for that feedback.
    strictFilters: false,
    ownPropertyOnly: true,
    greedy: false,
    root: [],
    parseLimit: 500_000,
    renderLimit: 1_000,
    memoryLimit: 1e7,
  });

  // `templates: {}` is not enough — liquidjs falls through to a filesystem
  // loader, and under `npm:liquidjs` in Deno that is a real disk read. Throwing
  // at construction makes it a PARSE error, so a bad template is rejected
  // before it can ever reach a live call.
  for (const name of BLOCKED_TAGS) {
    engine.registerTag(
      name,
      class Blocked {
        constructor() {
          throw new Error(`{% ${name} %} is not allowed in a system prompt`);
        }
        *render() {}
      } as never,
    );
  }

  return engine;
}

let _engine: Liquid | null = null;
function engine(): Liquid {
  if (!_engine) _engine = createPromptEngine();
  return _engine;
}

// ---------------------------------------------------------------------------
// current_time pre-pass
// ---------------------------------------------------------------------------

// `[^{}]` rather than `.` so an unclosed brace cannot run to the next `}}`.
const CURRENT_TIME_RE = /\{\{\s*current_time_([^{}]+?)\s*\}\}/g;

/**
 * Blank out `{{current_time_<TZ>}}` while PRESERVING every offset.
 *
 * `{{current_time_America/New_York}}` is a hard parse error in Liquid (the `/`
 * is division) and it is a shipped Verbeo feature, so it can never reach the
 * parser. Analysis needs the replacement to be the same length as the original,
 * because any length change desyncs every {row, col} liquidjs reports — which
 * would put the editor's error marker and click-to-focus on the wrong character.
 */
export function stripSystemPlaceholders(src: string): string {
  return (src || "").replace(CURRENT_TIME_RE, (m) => " ".repeat(m.length));
}

/**
 * Substitute a real timestamp. For preview/render, where offsets don't matter.
 *
 * `unknownTimezone` mirrors the Python side: "keep" leaves an unresolvable
 * token exactly as written (what the old substituter always did, so the legacy
 * path must keep doing it), "blank" drops it (required before Liquid parses,
 * since a surviving token is a syntax error).
 */
export function resolveSystemPlaceholders(
  src: string,
  now?: Date,
  unknownTimezone: "blank" | "keep" = "blank",
): string {
  return (src || "").replace(CURRENT_TIME_RE, (match, tz: string) => {
    try {
      // Mirrors python's "%A, %B %d, %Y at %I:%M:%S %p %Z".
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz.trim(),
        weekday: "long",
        month: "long",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZoneName: "short",
      }).formatToParts(now ?? new Date());
      const g = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
      return `${g("weekday")}, ${g("month")} ${g("day")}, ${g("year")} at ` +
        `${g("hour")}:${g("minute")}:${g("second")} ${g("dayPeriod")} ${g("timeZoneName")}`;
    } catch {
      return unknownTimezone === "keep" ? match : "";
    }
  });
}

// ---------------------------------------------------------------------------
// Conditional spans
// ---------------------------------------------------------------------------

const OPENING_TAGS = new Set(["if", "unless", "case"]);
const CLOSING_TAGS = new Set(["endif", "endunless", "endcase"]);

/**
 * Byte ranges covered by an outermost `{% if %}` / `{% unless %}` / `{% case %}`
 * block, INCLUSIVE of the opening tag token itself.
 *
 * Hand-rolled rather than walked off liquidjs's AST for two reasons. First,
 * liquidjs stores an `if` condition on the Tag, not in `branch.templates`, so
 * the guard occurrence is in no subtree — a naive walk classifies `first_name`
 * in `{% if first_name %}` as REQUIRED, which is exactly the variable that must
 * be optional. Second, this touches no liquidjs internals, so it survives the
 * patch bumps that package needs (16 advisories in 2026 alone).
 *
 * Including the opening tag in the span is what makes the guard variable
 * optional with no special case, and pulls `{% elsif %}` conditions in for free.
 */
export function conditionalSpans(src: string): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  const text = src || "";
  let depth = 0;
  let spanStart = 0;
  let i = 0;

  while (i < text.length) {
    if (text.startsWith("{% raw %}", i) || text.startsWith("{%- raw -%}", i)) {
      const end = text.indexOf("endraw", i);
      i = end < 0 ? text.length : text.indexOf("%}", end) + 2 || text.length;
      continue;
    }
    if (text.startsWith("{%", i)) {
      const tagStart = i;
      i += 2;
      // Skip to the tag name, tolerating `{%-` and whitespace.
      while (i < text.length && (text[i] === "-" || /\s/.test(text[i]))) i++;
      let n = i;
      while (n < text.length && /[a-z_]/i.test(text[n])) n++;
      const name = text.slice(i, n).toLowerCase();

      // Find the tag's close, skipping over quoted strings.
      let j = n;
      let quote = "";
      while (j < text.length) {
        const ch = text[j];
        if (quote) {
          if (ch === quote) quote = "";
        } else if (ch === '"' || ch === "'") {
          quote = ch;
        } else if (ch === "%" && text[j + 1] === "}") {
          break;
        }
        j++;
      }
      const tagEnd = Math.min(j + 2, text.length);

      if (OPENING_TAGS.has(name)) {
        if (depth === 0) spanStart = tagStart;
        depth++;
      } else if (CLOSING_TAGS.has(name)) {
        depth = Math.max(0, depth - 1);
        if (depth === 0) spans.push([spanStart, tagEnd]);
      }
      i = tagEnd;
      continue;
    }
    i++;
  }

  // An unclosed block runs to the end of the document — the same thing the
  // parser will conclude, and the analysis must not disagree with it.
  if (depth > 0) spans.push([spanStart, text.length]);
  return spans;
}

/**
 * Ranges of `{{ x | default: "y" }}` output statements.
 *
 * A reference carrying a `default:` filter states its own fallback, so it is as
 * handled as one inside `{% if %}` — demanding it from a web-widget visitor
 * would block a call to collect something the prompt already copes without.
 */
export function defaultedOutputSpans(src: string): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  for (const m of (src || "").matchAll(/\{\{[^{}]*\}\}/g)) {
    if (/\|\s*default\s*:/.test(m[0])) {
      spans.push([m.index ?? 0, (m.index ?? 0) + m[0].length]);
    }
  }
  return spans;
}

const inAnySpan = (offset: number, spans: Array<[number, number]>) =>
  spans.some(([a, b]) => offset >= a && offset < b);

/** Absolute offsets of each line start, for mapping liquidjs's {row, col}. */
function lineStarts(src: string): number[] {
  const starts = [0];
  for (let i = 0; i < src.length; i++) if (src[i] === "\n") starts.push(i + 1);
  return starts;
}

const toOffset = (starts: number[], row: number, col: number) =>
  (starts[Math.max(0, row - 1)] ?? 0) + Math.max(0, col - 1);

/**
 * Normalise a liquidjs parse/render error into a position we can point at.
 *
 * liquidjs exposes `error.token.begin` as an absolute offset — `token.line` and
 * `token.col` are undefined in 10.27.2 despite appearing in the message text.
 * The offset is the better anchor anyway, and because stripSystemPlaceholders
 * is length-preserving it maps straight onto the untransformed source.
 */
function toSyntaxError(e: unknown, src: string): PromptSyntaxError {
  const err = e as { message?: string; token?: { begin?: number } };
  const starts = lineStarts(src);
  const raw = String(err?.message ?? e).split("\n")[0];

  let offset = typeof err?.token?.begin === "number" ? err.token.begin : -1;
  if (offset < 0) {
    const m = raw.match(/line:(\d+),\s*col:(\d+)/);
    offset = m ? toOffset(starts, Number(m[1]), Number(m[2])) : 0;
  }
  offset = Math.max(0, Math.min(offset, src.length));

  let row = 1;
  while (row < starts.length && starts[row] <= offset) row++;
  return {
    // The message already ends with ", line:N, col:M"; the UI renders those
    // from row/col itself, so drop the duplicate.
    message: raw.replace(/,?\s*line:\d+,\s*col:\d+\s*$/, ""),
    row,
    col: offset - starts[row - 1] + 1,
    offset,
  };
}

// ---------------------------------------------------------------------------
// Lint
// ---------------------------------------------------------------------------

const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
const DOTTED_PATH_RE = /^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)+$/;

/** Spans of `{{ ... }}` output statements, for identifier checking. */
const OUTPUT_SPAN_RE = /\{\{([^{}]*)\}\}/g;
/** Spans of `{% ... %}` tags, so we only lint expressions, never prose. */
const TAG_SPAN_RE = /\{%([^{}]*?)%\}/g;

const BLANK_CMP_RE = /(?:!=|==)\s*(blank|empty|nil|null)\b|\b(?:blank|empty|nil|null)\s*(?:!=|==)/;
const NUMERIC_CMP_RE = /(?:==|!=|>=|<=|>|<)\s*-?\d+(?:\.\d+)?\s*(?:%\}|\s|$)/;

function lintPrompt(src: string): PromptLintWarning[] {
  const warnings: PromptLintWarning[] = [];
  const seen = new Set<string>();
  const push = (w: PromptLintWarning) => {
    const key = `${w.rule}:${w.offset}`;
    if (!seen.has(key)) {
      seen.add(key);
      warnings.push(w);
    }
  };

  // Output-statement rules. Driven off the source rather than the analyzer so
  // they also fire in legacy mode and on templates that fail to parse.
  for (const m of src.matchAll(OUTPUT_SPAN_RE)) {
    const inner = m[1].split("|")[0].trim();
    const offset = m.index ?? 0;
    const length = m[0].length;

    if (!inner || IDENTIFIER_RE.test(inner)) continue;
    if (inner.startsWith("current_time_")) continue;
    // Quoted literals, numbers, and bracket-quoted keys are all deliberate.
    if (/^["'\d[(]/.test(inner)) continue;

    // nested-path — dynamic variables are a flat key/value map. Linted in
    // legacy mode too on purpose: `{{a.b}}` is invisible to every current
    // regex and is read aloud verbatim today, so adding one unrelated
    // `{% if %}` elsewhere would silently flip it to rendering empty.
    if (DOTTED_PATH_RE.test(inner)) {
      push({
        rule: "nested-path",
        message:
          `{{ ${inner} }} won't work. Dynamic variables are a flat list of ` +
          `key/value pairs, so there is no "${inner.split(".")[0]}" object to ` +
          `read from. Use a single name like {{ ${inner.replace(/\./g, "_")} }}.`,
        offset,
        length,
      });
      continue;
    }

    // invalid-identifier — the only rule that closes a preview-vs-runtime gap.
    // liquidjs ACCEPTS `{{ first name }}` and `{{ नाम }}`; python-liquid rejects
    // both, so without this warning the preview renders perfectly while the
    // live call quietly falls back to plain substitution.
    push({
      rule: "invalid-identifier",
      message:
        `"${inner}" is not a usable variable name. Use letters, numbers and ` +
        `underscores only (for example first_name) — a name with spaces or ` +
        `non-Latin characters is left in the prompt as-is on the call.`,
      offset,
      length,
    });
  }

  // Expression-only rules. Never scan the whole document — prompts say the
  // word "blank" in prose constantly.
  for (const m of src.matchAll(TAG_SPAN_RE)) {
    const expr = m[1];
    if (BLANK_CMP_RE.test(expr)) {
      push({
        rule: "blank-comparison",
        message:
          `Use {% if name %} instead of comparing against blank. Verbeo ` +
          `strips empty values before rendering, so a plain {% if name %} ` +
          `already means "supplied and not empty" — and comparisons against ` +
          `blank/empty/nil are the one construct that can behave differently ` +
          `here than on the live call.`,
        offset: m.index ?? 0,
        length: m[0].length,
      });
    }
    if (NUMERIC_CMP_RE.test(expr)) {
      push({
        rule: "numeric-comparison",
        message:
          `Dynamic variables are always text, so comparing against a bare ` +
          `number is never true. Quote it — for example attempt == "2".`,
        offset: m.index ?? 0,
        length: m[0].length,
      });
    }
  }

  return warnings.sort((a, b) => a.offset - b.offset);
}

// ---------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------

const isUserVariable = (name: string) =>
  !SYSTEM_VARIABLES.has(name) && !name.startsWith("current_time_");

/**
 * Parse a system prompt and report what it needs. Never throws.
 *
 * Called from dialogs that analyse OTHER agents' prompts, which may contain
 * anything, so a throw here would break an unrelated screen.
 */
export function analyzePrompt(src: string): PromptAnalysis {
  const text = src || "";
  const legacyVars = extractDynamicVariables(text);
  const mode: PromptMode = usesLiquidSyntax(text) ? "liquid" : "legacy";

  const warnings = lintPrompt(text);

  // A prompt we cannot parse still has to report its variables, or a dialog
  // analysing someone else's agent renders an empty form over a typo.
  const legacy = (error: PromptSyntaxError | null): PromptAnalysis => ({
    mode,
    error,
    variables: legacyVars,
    required: legacyVars,
    optional: [],
    warnings,
  });

  if (mode === "legacy") return legacy(null);

  const masked = stripSystemPlaceholders(text);
  let globals: Record<
    string,
    Array<{ segments?: unknown[]; location?: { row: number; col: number } }>
  >;
  try {
    // deno-lint-ignore no-explicit-any
    globals = (engine().analyzeSync(engine().parse(masked), { partials: false }) as any).globals;
  } catch (e) {
    return legacy(toSyntaxError(e, text));
  }

  // "Guarded" = the prompt already says what to do without this value, either
  // because the reference sits inside a condition or because it carries a
  // `default:` fallback. Guarded references never make a variable required.
  const spans = [...conditionalSpans(text), ...defaultedOutputSpans(text)];
  const starts = lineStarts(text);
  const variables: string[] = [];
  const required = new Set<string>();

  for (const [name, refs] of Object.entries(globals ?? {})) {
    if (!isUserVariable(name)) continue;
    variables.push(name);
    for (const ref of refs ?? []) {
      const loc = ref.location;
      // No position means we cannot prove it is guarded, so treat it as
      // required. Over-requiring nags; under-requiring loses call data.
      if (!loc || !inAnySpan(toOffset(starts, loc.row, loc.col), spans)) {
        required.add(name);
      }
    }
  }

  return {
    mode,
    error: null,
    variables,
    required: variables.filter((v) => required.has(v)),
    optional: variables.filter((v) => !required.has(v)),
    warnings,
  };
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

/**
 * Build the render context: blank means ABSENT, every value is a string.
 *
 * Mirrors `build_context` in pipecat/prompt_template.py exactly. Liquid
 * natively treats "" as TRUTHY, so without the blank-stripping here
 * `{% if first_name %}` would fire on an empty value and the agent would greet
 * the caller by no name at all. Stringifying also removes the python/JS
 * divergences on floats and objects.
 */
export function buildRenderContext(
  values: Record<string, unknown>,
): Record<string, string> {
  const ctx: Record<string, string> = {};
  for (const [key, value] of Object.entries(values ?? {})) {
    if (!key || value === null || value === undefined) continue;
    let text: string;
    if (typeof value === "boolean") text = value ? "true" : "false";
    else if (typeof value === "object") text = JSON.stringify(value);
    else text = String(value);
    if (!text.trim()) continue; // BLANK IS FALSY
    ctx[key] = text;
  }
  return ctx;
}

export interface PromptPreviewResult {
  ok: boolean;
  /** The rendered prompt, or "" when `error` is set. */
  text: string;
  error: PromptSyntaxError | null;
}

/**
 * Render for the dashboard preview. Never throws.
 *
 * Deliberately not a discriminated union: the React app compiles with
 * `strict: false`, where narrowing on an `ok: true | false` discriminant does
 * not hold inside JSX branches.
 */
const LEGACY_VAR_RE = /\{\{\s*(\w+)\s*\}\}/g;

/**
 * What the OLD substituter did: replace `{{name}}` when we have it, leave the
 * token alone when we don't. Used for prompts that never opted in to Liquid, so
 * the preview shows what the bot will actually produce rather than what Liquid
 * would have produced.
 */
function renderLegacy(src: string, values: Record<string, unknown>): string {
  const ctx: Record<string, string> = {};
  for (const [k, v] of Object.entries(values ?? {})) {
    if (v === null || v === undefined) continue;
    ctx[k] = typeof v === "object" ? JSON.stringify(v) : String(v);
  }
  // The bot blanks any token still standing after substitution, so mirror that
  // — the preview is headed "what the agent will be given".
  return src.replace(LEGACY_VAR_RE, (m, n) => (n in ctx ? ctx[n] : m))
    .replace(LEGACY_VAR_RE, "");
}

export function renderPromptPreview(
  src: string,
  values: Record<string, unknown>,
  now?: Date,
): PromptPreviewResult {
  if (!usesLiquidSyntax(src || "")) {
    return {
      ok: true,
      text: renderLegacy(resolveSystemPlaceholders(src || "", now, "keep"), values),
      error: null,
    };
  }
  const prepared = resolveSystemPlaceholders(src || "", now);
  try {
    return {
      ok: true,
      text: engine().parseAndRenderSync(prepared, buildRenderContext(values)),
      error: null,
    };
  } catch (e) {
    return { ok: false, text: "", error: toSyntaxError(e, src || "") };
  }
}

export { SYSTEM_VARIABLES };
